import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useFragment } from "@apollo/client/react";
import { FETCH_CARDS } from "@ppb/tbd-store/actions/catalogue";
import {
  PebbleCardGroupBodyFragment,
  PebbleCardGroupSelectionFragment,
} from "@ppb/tbd-components-sports-betting/components/PebbleCardGroup/model/PebbleCardGroup.graphql";

type PebbleItem = { urn: string; typename: string };

/**
 * Mirrors the container's selection precedence: persisted `@client` selection, then the server's
 * `selectedItemUrn`, then the first supported pebble — each only accepted if it is one of this
 * group's items. Reading `selectedItemUrn` alone would keep fetching the server's pebble after the
 * user switches.
 */
const resolveSelectedUrn = (
  items: PebbleItem[],
  persistedUrn: string | null,
  serverUrn: string | null,
  isSupportedTypename: (typename: string) => boolean,
  canFallBack: boolean,
): string | null => {
  const isOneOfItems = (candidate: string | null): candidate is string =>
    !!candidate && items.some((item) => item.urn === candidate);

  if (isOneOfItems(persistedUrn)) {
    return persistedUrn;
  }
  if (isOneOfItems(serverUrn)) {
    return serverUrn;
  }

  // Only once the body has landed. The items reach the cache before `selectedItemUrn` does — an enriched
  // partial carries them through the header-time write — so falling back any earlier fetches the first
  // pebble's card and then fetches again for the real selection.
  return canFallBack ? items.find((item) => !!item.typename && isSupportedTypename(item.typename))?.urn ?? null : null;
};

/**
 * Fetches this group's selected inner card whether or not the group is expanded — a collapsed group
 * is still required to hold its selected card's body.
 *
 * Which is why the fetch lives here and not in the content slot: that slot renders inside the
 * container's collapsible Card, and the-wall's `Collapse` defaults to `unmountOnClose`, so a
 * collapsed group never mounts it. Keep this the only dispatch point — the saga skips urns already in
 * flight, but a request that has already landed is not deduped and a paginating reducer appends a
 * duplicated first page, so the guard on the resolved urn is what keeps it to one fetch per selection.
 */
export const useSelectedCardFetch = (
  cardGroupUrn: string,
  isSupportedTypename: (typename: string) => boolean,
  // The container's isPebbleAvailable. Both resolve the selection, so a pebble dropped from the strip but
  // kept here would be the one fetched, and the pebble on screen never would.
  isPebbleAvailable: (urn: string, typename: string) => boolean = () => true,
): void => {
  const dispatch = useDispatch();
  const from = { __typename: "PebbleCardGroup" as const, urn: cardGroupUrn };

  const { data: body, complete: bodyComplete } = useFragment({
    fragment: PebbleCardGroupBodyFragment,
    fragmentName: "PebbleCardGroupBody",
    from,
  });
  const { data: selection } = useFragment({
    fragment: PebbleCardGroupSelectionFragment,
    fragmentName: "PebbleCardGroupSelection",
    from,
  });

  const items = (body?.partials?.edges ?? []).flatMap((edge) => {
    const node = edge?.node;

    if (!node?.urn) {
      return [];
    }

    const item = { urn: node.urn, typename: node.__typename ?? "" };

    return isPebbleAvailable(item.urn, item.typename) ? [item] : [];
  });

  const selectedUrn = resolveSelectedUrn(
    items,
    selection?.selectedNavigationPebbleUrn ?? null,
    body?.selectedItemUrn ?? null,
    isSupportedTypename,
    bodyComplete,
  );

  const fetchedUrn = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedUrn || fetchedUrn.current === selectedUrn) {
      return;
    }
    fetchedUrn.current = selectedUrn;
    dispatch({ type: FETCH_CARDS, payload: { urns: [selectedUrn] } });
  }, [dispatch, selectedUrn]);
};
